import React, { FC } from 'react';
import { CircleTopicBaseFragment } from '@modernhealth/circles-graphql/v2';
import { FormattedDisplayName } from 'react-intl';
import { Trans } from 'react-i18next';

import { captureException } from 'src/shared/helpers/captureException';
import { CircleContentCollectionRenderComponent } from 'src/generated/webapp_gql';
import { Grid } from 'src/shared/components/Grid';

import { CirclesScreenNodeSectionFragment } from '../documents/circlesLanguageOverviewScreen.generated';

import { LanguageMenuCard } from './CirclesNonEnglishCirclesSection';
import { CirclePageSection } from './CirclePageSection';
import { UnorderedList } from './UnorderedList';
import { CircleOnDemandCard } from './CircleOnDemandCard';
import { CircleTopicCard } from './CircleTopicCard';

const Language = ({ code }: { code?: string }) => {
    return <FormattedDisplayName type="language" value={code ?? 'en'} />;
};

const TranslatedHeader = ({ i18nKey }: { i18nKey: string }) => {
    return (
        <Trans
            t={header => header}
            i18nKey={i18nKey}
            components={{
                language: <Language />,
            }}
        />
    );
};

export const CirclesRenderScreenSectionNode: FC<{
    section: CirclesScreenNodeSectionFragment | null;
}> = ({ section }) => {
    if (!section) return null;

    const renderContent = () => {
        switch (section.renderComponent) {
            case CircleContentCollectionRenderComponent.Grid:
                return (
                    <Grid as={UnorderedList} minItemWidth={300} expand={false} maxColumns={3}>
                        {mapCirclesSectionNodeTypeToSectionNodeComponent(section)}
                    </Grid>
                );
            default:
                captureException(new Error('RenderComponent type is not recognized'), {
                    type: section.renderComponent,
                });
                return null;
        }
    };

    return (
        <CirclePageSection
            header={<TranslatedHeader i18nKey={section.title} />}
            description={section.subtitle || undefined}
            cta={section.ctaLinkText || undefined}
            to={mapCirclesSectionNodeTypeToLink(section)}
        >
            {renderContent()}
        </CirclePageSection>
    );
};

const mapCirclesSectionNodeTypeToLink = (section: CirclesScreenNodeSectionFragment) => {
    switch (section.ctaLink?.__typename) {
        case 'CircleCircleTopicLinkNode':
            return `/circles/${section.ctaLink.circleTopic?.slug}`;
        case 'CircleLanguageLinkNode':
            return `/circles/language/${section.ctaLink.circleLanguage?.languageCode}`;
        case 'CircleOnDemandLinkNode':
            return `/circles/on-demand/${section.ctaLink.circleOnDemand?.slug}`;
        default:
            return undefined;
    }
};

const mapCirclesSectionNodeTypeToSectionNodeComponent = (
    section: CirclesScreenNodeSectionFragment,
) => {
    return section.links.map((link, i) => {
        switch (link?.__typename) {
            case 'CircleCircleTopicLinkNode':
                if (!link.circleTopic) {
                    return null;
                }
                return (
                    <CircleTopicCard
                        id={`circle-topic-card-${section.id}-${link.circleTopic.name}`}
                        key={`circle-topic-card-${section.id}-${link.circleTopic.name}`}
                        topic={link.circleTopic as CircleTopicBaseFragment}
                    />
                );
            case 'CircleLanguageLinkNode':
                if (!link.circleLanguage) {
                    return null;
                }
                return (
                    <LanguageMenuCard
                        key={`${section.id}-${link.circleLanguage.languageCode}`}
                        backgroundColor="black"
                        backgroundColorOnHover="black"
                        languageCode={link.circleLanguage.languageCode}
                    />
                );
            case 'CircleOnDemandLinkNode':
                if (!link.circleOnDemand) {
                    return null;
                }
                const ondemand = link.circleOnDemand;
                return (
                    <CircleOnDemandCard
                        key={`${ondemand.id}-${i}`}
                        slug={ondemand?.slug}
                        name={ondemand.name}
                        thumb={ondemand.video.thumbnail}
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                    />
                );
            default:
                return undefined;
        }
    });
};
